import { ComponentPropsWithoutRef, useRef } from 'react';

import { useComboBoxState } from '@react-stately/combobox';
import { useComboBox } from '@react-aria/combobox';

import type { ComboBoxStateOptions } from '@react-stately/combobox';
import type { AriaComboBoxOptions } from '@react-aria/combobox';

import { Box, Textfield, LoadingSpinner } from '@simplywallst/ui-core';

import { Popover } from './Popover';
import { ListBox } from './ListBox';
import { EmptyState } from './EmptyState';

interface Props<T>
  extends Pick<
      ComboBoxStateOptions<T>,
      | 'items'
      | 'menuTrigger'
      | 'selectedKey'
      | 'placeholder'
      | 'children'
      | 'isDisabled'
      | 'inputValue'
      | 'onInputChange'
      | 'onSelectionChange'
    >,
    Pick<AriaComboBoxOptions<T>, 'placeholder' | 'label' | 'onFocus'> {
  emptyStateRender?: ComponentPropsWithoutRef<
    typeof ListBox
  >['emptyStateRender'];
  status: 'pending' | 'success' | 'error';
}

export const ComboBox = <T extends object>({
  label,
  items,
  selectedKey,
  menuTrigger = 'focus',
  children,
  inputValue,
  isDisabled = false,
  placeholder,
  emptyStateRender = () => <EmptyState />,
  onInputChange,
  onSelectionChange,
  status,
}: Props<T>) => {
  const state = useComboBoxState({
    children,
    items,
    menuTrigger,
    inputValue,
    selectedKey,
    onInputChange,
    onSelectionChange,
    allowsEmptyCollection: true,
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const listBoxRef = useRef<HTMLUListElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const { inputProps, listBoxProps } = useComboBox(
    {
      label,
      isDisabled,
      placeholder,
      inputRef,
      listBoxRef,
      popoverRef,
      autoFocus: true,
    },
    state
  );

  const { isOpen, close, collection } = state;

  const isIdleState =
    (state.inputValue.length === 0 || status === 'pending') &&
    collection.size === 0;

  return (
    <Box width="100%" position="relative" backgroundColor="white">
      <Textfield
        {...inputProps}
        ref={inputRef}
        type="text"
        styleType="standardReverse"
        postfix={
          Number(inputValue?.length) && status === 'pending' ? (
            <LoadingSpinner />
          ) : null
        }
      />
      {isOpen && (
        <Popover
          ref={popoverRef}
          state={state}
          triggerRef={inputRef}
          isNonModal
          placement="bottom start"
        >
          {!isIdleState ? (
            <ListBox
              {...listBoxProps}
              close={close}
              ref={listBoxRef}
              state={state}
              emptyStateRender={emptyStateRender}
            />
          ) : null}
        </Popover>
      )}
    </Box>
  );
};
